// Cờ non-interactive của cụm Chatops/Mattermost (T2) — dùng chung cho hai đường:
// `avatar comm-connect` (7 điểm hỏi) và nhánh comm của `avatar init` (10 điểm hỏi).
//
// ⚠️ QUYẾT ĐỊNH L4 (chốt khi thi công, hướng (a) + fail-fast):
// site `run-comm-setup-phase.ts:40` ("có muốn setup không?") KHÔNG với tới được
// bằng `--comm-skip` — cờ đó bị xử ở orchestrator và bỏ TRỌN phase, không bao giờ
// chạm `:40`. Nên T2 khai cờ MỚI `--comm-setup <yes|no>` đọc đúng tại `:40`:
//   · `no`     → bỏ phase êm, giống người dùng trả lời "không"
//   · `yes`    → chạy tiếp, và khi đó các cờ còn lại thành bắt buộc
//   · vắng mặt → `MissingFlagError --comm-setup` (không đoán hộ người dùng)
// `--comm-skip` giữ nguyên nghĩa cũ và vẫn thắng, vì nó chặn từ orchestrator.
//
// ⚠️ QUYẾT ĐỊNH TOKEN: ở chế độ non-interactive, `--comm-bot-token-stdin` là BẮT
// BUỘC — không rơi ngầm về token trong keychain. Lý do: máy gọi không nhìn thấy
// keychain có gì, dùng ngầm một credential không ai khai là đúng loại lỗi khó truy
// nhất; và ca âm phụ thuộc keychain thì không hermetic (keychain là toàn máy, không
// theo HOME tạm của test).

import type { Command } from "commander";
import { MissingFlagError, requireFlag } from "./non-interactive-context.js";
import { readSecretIfRequested } from "./read-secret-from-stdin.js";

/** Nền tảng chat hỗ trợ. Danh sách một phần tử — chừa chỗ mở rộng, không phải thừa. */
export const COMM_PROVIDER_VALUES = ["mattermost"] as const;

/** Cách xử khi validate token/channel thất bại ở chế độ không có người trả lời. */
export const COMM_VALIDATE_FAIL_ACTIONS = ["abort", "defer"] as const;

export type CommValidateFailAction = (typeof COMM_VALIDATE_FAIL_ACTIONS)[number];

/** Giá trị cờ, tên camelCase đúng như commander sinh ra. */
export interface CommSetupFlags {
  commSetup?: string;
  commProvider?: string;
  mattermostUrl?: string;
  commBotName?: string;
  commChannel?: string;
  commTeam?: string;
  commBotTokenStdin?: boolean;
  onCommValidateFail?: string;
}

/** Giá trị đã kiểm đủ, sẵn sàng dùng — không còn `undefined` ở field bắt buộc. */
export interface ResolvedCommFlags {
  botName: string;
  channelName: string;
  team?: string;
  botToken: string;
  onValidateFail: CommValidateFailAction;
}

/** Đăng ký nhóm cờ chung cho cả `comm-connect` lẫn `init`. */
export function registerCommSetupFlags(cmd: Command, options: { withProvider: boolean }): Command {
  if (options.withProvider) {
    cmd
      .option("--comm-setup <yes|no>", "Có chạy phase Chatops không (thay câu hỏi đầu phase)")
      .option(`--comm-provider <${COMM_PROVIDER_VALUES.join("|")}>`, "Nền tảng chat")
      .option("--mattermost-url <url>", "Base URL Mattermost");
  }
  return cmd
    .option("--comm-bot-name <name>", "Tên bot Mattermost")
    .option("--comm-channel <name>", "Tên channel (hoặc team/channel)")
    .option("--comm-team <name>", "Team slug — bắt buộc khi channel trùng tên ở nhiều team")
    .option("--comm-bot-token-stdin", "Đọc bot token từ stdin (không qua argv)")
    .option(
      `--on-comm-validate-fail <${COMM_VALIDATE_FAIL_ACTIONS.join("|")}>`,
      "Xử lý khi validate thất bại (mặc định abort)",
    );
}

/** Đọc + kiểm `--on-comm-validate-fail`. Vắng mặt = `abort` (mặc định an toàn). */
export function resolveValidateFailAction(raw: string | undefined): CommValidateFailAction {
  if (raw === undefined || raw === "") return "abort";
  if (!(COMM_VALIDATE_FAIL_ACTIONS as readonly string[]).includes(raw)) {
    throw new Error(
      `--on-comm-validate-fail "${raw}" không hợp lệ. Chọn: ${COMM_VALIDATE_FAIL_ACTIONS.join(", ")}`,
    );
  }
  return raw as CommValidateFailAction;
}

/**
 * Kiểm đủ cờ + đọc token từ stdin. Ném ở cờ ĐẦU TIÊN còn thiếu.
 *
 * Gọi TRƯỚC mọi lời gọi mạng: ca âm nhờ vậy hermetic, và người dùng biết ngay
 * mình thiếu gì thay vì đợi một vòng HTTP rồi mới bị mắng.
 */
export async function resolveCommFlagsOrThrow(
  flags: CommSetupFlags,
  command: string,
): Promise<ResolvedCommFlags> {
  // Kiểm GIÁ TRỊ trước khi kiểm SỰ CÓ MẶT: `--on-comm-validate-fail xyz` là typo,
  // báo ngay dù người dùng còn thiếu cờ khác — bắt lỗi theo thứ tự gõ lệnh thì
  // họ phải sửa nhiều vòng mới thấy cái typo đứng cuối.
  const onValidateFail = resolveValidateFailAction(flags.onCommValidateFail);

  const botName = requireFlag(flags.commBotName, {
    flag: "--comm-bot-name",
    command,
    hint: "--comm-bot-name avatar",
  });
  const channelName = requireFlag(flags.commChannel, {
    flag: "--comm-channel",
    command,
    hint: "--comm-channel team/channel",
  });
  requireFlag(flags.commBotTokenStdin, {
    flag: "--comm-bot-token-stdin",
    command,
    hint: `echo "$TOKEN" | avatar ${command} --non-interactive --comm-bot-token-stdin …`,
  });
  const botToken = await readSecretIfRequested(true);
  if (botToken === undefined) {
    throw new MissingFlagError({
      missingFlag: "--comm-bot-token-stdin",
      command,
      hint: 'stdin rỗng — truyền token qua pipe, ví dụ: echo "$TOKEN" | avatar …',
    });
  }

  return { botName, channelName, team: flags.commTeam, botToken, onValidateFail };
}

/** Kiểm `--comm-provider` cho nhánh `init` (site `run-comm-setup-phase.ts:48`). */
export function requireCommProvider(flags: CommSetupFlags, command: string): string {
  const provider = requireFlag(flags.commProvider, {
    flag: "--comm-provider",
    command,
    hint: `--comm-provider ${COMM_PROVIDER_VALUES.join("|")}`,
  });
  if (!(COMM_PROVIDER_VALUES as readonly string[]).includes(provider)) {
    throw new Error(
      `--comm-provider "${provider}" không hợp lệ. Chọn: ${COMM_PROVIDER_VALUES.join(", ")}`,
    );
  }
  return provider;
}

/** Kiểm `--comm-setup` (L4). Trả `true` nếu phải chạy phase. */
export function requireCommSetupDecision(flags: CommSetupFlags, command: string): boolean {
  const raw = requireFlag(flags.commSetup, {
    flag: "--comm-setup",
    command,
    hint: "--comm-setup yes|no (hoặc --comm-skip để bỏ trọn phase)",
  });
  if (raw !== "yes" && raw !== "no") {
    throw new Error(`--comm-setup "${raw}" không hợp lệ. Chọn: yes, no`);
  }
  return raw === "yes";
}

/**
 * Chọn team từ danh sách trùng tên bằng `--comm-team`.
 *
 * KHÔNG tự lấy phần tử đầu: hai team khác nhau có thể cùng tên channel, đoán sai
 * là ghi credential trỏ nhầm nơi — hỏng im lặng, phát hiện rất muộn.
 */
export function pickTeamByFlag<T extends { teamName: string }>(
  candidates: T[],
  team: string | undefined,
  command: string,
): T {
  const chosen = requireFlag(team, {
    flag: "--comm-team",
    command,
    hint: `--comm-team ${candidates.map((c) => c.teamName).join("|")}`,
  });
  const found = candidates.find((c) => c.teamName === chosen);
  if (!found) {
    throw new Error(
      `--comm-team "${chosen}" không nằm trong danh sách team có channel này: ${candidates
        .map((c) => c.teamName)
        .join(", ")}`,
    );
  }
  return found;
}
